// @flow
import { List } from "immutable"
import _ from "lodash"
import moment from "moment"

import Booking from "@kiwicom/frontend-app/scripts/containers/mutable/Booking"
import apiBookingTokens from "mmb/services/apiBookingTokens/apiBookingTokens"
import cuckoo from "common/services/cuckoo/cuckoo"
import jsonFetch, { getUrlWithQuery } from "common/services/fetch/jsonFetch"
import type { Options } from "common/services/fetch/jsonFetch"
import constants from "common/consts/config"
import { cuckooEvents } from "common/consts/enums"
import Journey from "common/records/Journey"
import mapperJourney from "common/services/journey/mapperJourney"
import { getAirlines } from "common/services/staticData"
import authStore from "common/services/user/authStore"
import sniffError from "common/services/utils/sniffError"
import type { BookingCompletionPayload } from "mmb/types/outbound"
import SearchFormState from "common/records/SearchFormState"
import type { ViaSupportCategory } from "mmb/consts/additionalServices"
import { API_CATEGORIES } from "mmb/consts/booking"
import type { BookingInput } from "mmb/records/bookingInput"
import type { TicketingFormData } from "mmb/scenes/AccountBooking/components/Booking/components/BookingTicketing/services/validateTicketingForm"
import type {
  ManageScheduleChangeApiPayload,
  SelectedScheduleChangeApiPayload,
} from "mmb/scenes/AccountBooking/components/Booking/scenes/BookingScheduleChanges/consts/PropTypes"
import mapperManageBooking from "mmb/scenes/AccountBooking/components/Booking/services/mapper/mapperManageBooking"
import mapperScheduleChange from "mmb/scenes/AccountBooking/components/Booking/services/mapper/services/mapperScheduleChange"
import type { seatingApiPayload } from "mmb/scenes/AccountBooking/components/Booking/services/seating/helpers"
import { mapToMamboPayload } from "mmb/scenes/AccountBooking/components/Booking/services/seating/helpers"
import type { BookingId, FlightId } from "mmb/types/primitiveTypes"
import createSearchParams from "./createSearchParams"
import mapperTransaction from "./mapperTransaction"
import mapperAncillaries from "mmb/scenes/AccountBooking/components/Booking/services/mapper/services/mapperAncillaries"
import mapperAncillariesDocuments from "mmb/scenes/AccountBooking/components/Booking/services/mapper/services/mapperAncillariesDocuments"
import type { FiltersValues } from "mmb/scenes/Account/services/AccountDuck.types"
import type {
  ConfirmFlightResponse,
  LoadGuaranteeResponse,
} from "mmb/scenes/AccountBooking/components/Booking/scenes/BookingPassengerAta/consts/types"
import trackInvalidBookingData from "mmb/scenes/AccountBooking/components/Booking/services/utils/trackInvalidBookingData"

const ROUTE = "api/v0.1/users/self/bookings"

export type additionalServicesRequestForm = {
  category: ViaSupportCategory,
  values: {
    message?: string,
    type?: string,
    width?: string,
    height?: string,
    weight?: string,
    different?: string,
  },
}

export type PassengerChanges = {
  passenger_changes: {
    [key: number | string]: {
      birthdate?: string,
      document_expiration?: string,
      document_number?: string,
      files?: string[],
      first_name?: string,
      last_name?: string,
      middle_name?: string,
      nationality?: string,
      title?: string,
    },
  },
}
export const HEADER_SIMPLE_TOKEN = "KW-Simple-Token"
export const HEADER_USER_TOKEN = "KW-User-Token"
export const HEADER_AUTH_TOKEN = "KW-Auth-Token"
// const HEADER_PARTNER_TOKEN = "KW-Partner-Token"

const apiUrl = (): string => window.reduxStore.getState().options.get("bookingApiUrl")

// TODO: Api should return errors with correct error status, not with status 200
const fetchWithSniff = (url: string, options: Options) =>
  jsonFetch(url, options).then(body => {
    const maybeError = sniffError(null, { body })
    if (maybeError) return Promise.reject(maybeError)
    return body
  })

export function transport(
  method: "POST" | "GET" | "DELETE" | "PATCH" | "PUT",
  bid?: number,
  path: string,
  query: any,
  payload?: any,
  retry: number = 0,
): Promise<any> {
  const isMamboApiCall = path.includes("mmb/v1")
  const { options } = window.reduxStore.getState()
  const fetchUrl = isMamboApiCall ? options.get("mmbApiUrl") : apiUrl()

  if (isMamboApiCall) {
    return fetchWithSniff(fetchUrl + path, {
      headers: {
        [HEADER_SIMPLE_TOKEN]: bid ? apiBookingTokens.getSimple(bid) : null,
        [HEADER_USER_TOKEN]: apiBookingTokens.getAuth(),
        [HEADER_AUTH_TOKEN]: authStore.kwAuthToken || "",
      },
      method,
      query,
      body: payload,
      retry,
    })
  }

  const queryPart = {
    brand: options.branding.get("id"),
    ...query,
    ...(bid ? apiBookingTokens.get(bid) : null),
  }
  return fetchWithSniff(fetchUrl + path, {
    headers: {
      [HEADER_AUTH_TOKEN]: authStore.kwAuthToken || "",
    },
    method,
    query: queryPart,
    body: payload,
    retry,
  })
}

export function detectSegment(booking: Booking, selected: Journey): ?number {
  const originalTrips = booking.journey.get("trips")
  const selectedTrips = selected.get("trips")
  if (originalTrips.size !== selectedTrips.size) {
    return originalTrips.indexOf(selectedTrips.first())
  }
  return undefined
}

function mapFiltersToApi(filters = {}) {
  const apiFilters = {}
  if (filters.dateFrom)
    apiFilters.date_from = moment(filters.dateFrom, "YYYY/MM/DD").format(constants.apiDateFormat)
  if (filters.dateTo)
    apiFilters.date_to = moment(filters.dateTo, "YYYY/MM/DD").format(constants.apiDateFormat)
  if (filters.status) apiFilters.status = filters.status // on bookings
  if (filters.type) apiFilters.type = filters.type // on watchdogs
  return apiFilters
}

export const findOne = (
  bid: BookingId,
  locale: string = "en",
  retry: number = 3,
  retryTimeout: number = 5000,
): Promise<Booking> => {
  // APMs payment can cause delay in saving booking => give 'status: null' after payment 3 attempts
  // (default number of retries in other cases)
  return transport("GET", bid, `/${ROUTE}/${bid}`, { locale }, null, 0).then(
    (booking: BookingInput) => {
      if (!booking.status && retry >= 0) {
        cuckoo.track(cuckooEvents.MMB_LOAD_BOOKING_STATUS_NULL, { numberOfRetries: -(retry - 3) })
        return new Promise(resolve => {
          setTimeout(() => {
            resolve(findOne(bid, locale, retry - 1, retryTimeout + 1000))
          }, retryTimeout)
        })
      }
      apiBookingTokens.add(booking.bid, booking.auth_token) // force using simple_token for loading bookings

      const mappedBooking = mapperManageBooking(booking)
      trackInvalidBookingData(mappedBooking)
      return mappedBooking
    },
  )
}

export const findSeats = (
  bid: BookingId,
  retry: number = 30,
  retryTimeout: number = 5000,
): Promise<any> =>
  transport("GET", bid, `/mmb/v1/bookings/${bid}/seatmaps`, undefined, undefined, retry).then(
    body => {
      if (body.status === "get_options_started" && retry > 0) {
        return new Promise(resolve => {
          setTimeout(() => {
            resolve(findSeats(bid, retry - 1, retryTimeout + 1000))
          }, retryTimeout)
        })
      }
      return body
    },
  )

export const getToken = (
  bid: BookingId,
  email: string,
  where: string,
  when: string,
): Promise<any> => {
  const query = {
    src: where,
    email,
    dtime: when,
  }

  return jsonFetch(`${apiUrl()}/api/v0.1/users/get_simple_token/${bid}`, {
    query,
  }).then(x => x.simple_token)
}

// $FlowFixMe
export const fetchScheduleChange = (bid: BookingId, retry: number): Promise<*> =>
  transport("GET", bid, `/mmb/v1/bookings/${bid}/alternatives`, undefined, undefined, retry).then(
    res => mapperScheduleChange(res.schedule_change),
  )

export const find = (locale: string, filters: FiltersValues = {}): Promise<Array<?Booking>> =>
  transport(
    "GET",
    undefined,
    `/${ROUTE}`,
    {
      ...mapFiltersToApi(filters),
      token: authStore.token,
      locale,
      limit: filters.limit || 5000,
      offset: filters.offset,
    },
    null,
    3,
  ).then((res: Array<?BookingInput>) =>
    _.map(res, booking => {
      // $FlowFixMe: error revealed by updating Flow and lodash libdef, please fix
      apiBookingTokens.add(booking.bid, booking.auth_token) // force using simple_token for loading bookings
      // $FlowFixMe: error revealed by updating Flow and lodash libdef, please fix
      return mapperManageBooking(booking)
    }),
  )

export const findTransactions = (filters: FiltersValues): Promise<any> =>
  transport(
    "GET",
    undefined,
    "/api/v0.1/users/self/transactions",
    { ...mapFiltersToApi(filters), token: authStore.token },
    null,
    3,
  ).then(res => _.map(res.transactions, transaction => mapperTransaction(transaction)))

export const edit = (bid: BookingId, payload: any) =>
  transport("POST", bid, `/${ROUTE}/edit/${bid}`, null, payload)

export const action = (actionName: string, bid: BookingId, data: any) =>
  transport("POST", bid, `/${ROUTE}/${bid}`, null, { action: actionName, data })

export const submitContact = (bid: BookingId, data: string) =>
  transport("POST", bid, `/mmb/v1/bookings/${bid}/contacts`, null, data)

export const deleteContact = (bid: BookingId, contactId: number) =>
  transport("DELETE", bid, `/mmb/v1/bookings/${bid}/contacts/${contactId}`)

export const changePrimaryEmail = (bid: BookingId, data: { email: string }) =>
  transport("PUT", bid, `/mmb/v1/bookings/${bid}/primary_email`, null, data)

export const downloadExcel = (locale: string, what: string, filters: FiltersValues) => {
  let url
  switch (what) {
    case "bookings":
      url = `${apiUrl()}/${ROUTE}?v=2`
      break
    case "transactions":
      url = `${apiUrl()}/api/v0.1/users/self/transactions?v=2`
      break
    default:
      break
  }

  if (!url) return
  // TODO: token in query :-(
  window.location.href = getUrlWithQuery(url, {
    ...mapFiltersToApi(filters),
    token: authStore.token,
    locale,
    format: "xlsx",
  })

  // const req = superagent.get(url).query(
  //   Object.assign({}, mapFiltersToApi(filters), {
  //     token: authStore.token,
  //     locale,
  //     format: "xlsx",
  //   }),
  // )
  // window.location.href = `${req.url}&${req["_query"][0]}`
}

export const additionalServicesRequest = (booking: Booking, form: additionalServicesRequestForm) =>
  transport("POST", booking.id, `/api/v0.1/additional_services_request/${booking.id}`, null, form)

export const additionalServicesSeatingRequest = (bid: BookingId, payload: seatingApiPayload) =>
  transport("POST", bid, `/mmb/v1/bookings/${bid}/seat_orders`, null, mapToMamboPayload(payload))

export const findAlternatives = (
  booking: Booking,
  selected: Journey,
  searchForm: SearchFormState,
  brandingId: string,
): Promise<any> => {
  let queryPayload = {
    bid: booking.id,
    limit: 25,
    // $FlowFixMe
    ...createSearchParams(searchForm),
    segment: detectSegment(booking, selected),
  }

  let partner
  if (brandingId === "kayakwhite") {
    partner = "kayakwl"
  } else if (brandingId === "wowairprime") {
    partner = "wowair"
  }

  if (partner) {
    queryPayload = {
      ...queryPayload,
      partner,
    }
  }

  // NOTE!!!
  // This is a terrible idea and only here as a god damn hotfix
  // If I see anyone else doing this shit he's gonna have to explain himself
  // in front of every FE dev eye to eye as a shaming ritual
  const airlines = getAirlines(window.__INITIAL_STATE__)
  return transport("GET", booking.id, "/api/v0.1/alternative_route", queryPayload).then(res =>
    List(res.data.map(j => mapperJourney(j, "", false, airlines))),
  )
}

// eslint-disable-next-line no-unused-vars
export const checkFlight = (booking: Booking, selected: Journey): Promise<any> => {
  // Disable check flights due to BE bug
  // https://gitlab.skypicker.com/booking/backend/issues/4321
  return Promise.resolve()

  /* eslint-disable no-unreachable */
  // $FlowFixMe
  return fetchWithSniff(`${apiUrl()}/api/v0.1/check_flights`, {
    query: {
      booking_token: selected.get("bookingToken"), // eslint-disable-line camelcase
      bnum: booking.bags,
      pnum: 1, // API nonsense - pnum is in "bookingToken"
    },
  })
  /* eslint-enable */
}

export const checkFlightPriceBeforeBooking = (token: string, currency: string): Promise<any> => {
  const query = {
    booking_token: token, // eslint-disable-line camelcase
    currency,
    bnum: 0,
    pnum: 1, // API nonsense - pnum is in "bookingToken"
  }
  return fetchWithSniff(`${apiUrl()}/api/v0.1/check_flights`, {
    query,
  }).then(json => json.conversation)
}

export const submitForm = (booking: Booking, selected: Journey, form: Booking) =>
  transport("POST", booking.id, `/api/v0.1/alternative_route_request/${booking.id}`, null, {
    ...form,
    segment: detectSegment(booking, selected),
  })

export const confirm = (bid: BookingId, payload: any) =>
  transport("POST", bid, `/api/v0.1/users/self/bookings/${bid}`, null, payload)

export const getThirdPartyAncillaries = (bid: number) =>
  transport("GET", bid, `/mmb/v1/bookings/${bid}/ancillaries`).then(res => mapperAncillaries(res))

export const getThirdPartyAncillariesDocuments = (bid: number, oid: number) =>
  transport("GET", bid, `/mmb/v1/bookings/${bid}/third_party_ancillary_order/${oid}`).then(res =>
    mapperAncillariesDocuments(res),
  )

export const cancelBooking = (bid: BookingId) => transport("DELETE", bid, `/mmb/v1/bookings/${bid}`)

export const submitPassengerEditFrom = (bid: BookingId, payload: PassengerChanges) =>
  transport("POST", bid, `/mmb/v1/bookings/${bid}/passengers/change_requests`, null, payload)

// Payload could be any to prevent flow failing. Not used yet in production.
export const orderNameChange = (bid: BookingId, payload: any) =>
  transport("POST", bid, `/api/v0.1/users/self/bookings/${bid}`, null, {
    action: "create",
    booked_at: payload.bookedAt,
    category: API_CATEGORIES.PASSENGER_EDIT,
    orig_price: payload.price,
    currency: payload.currency,
    passenger_change_request_id: payload.requestId,
    payment: { gate: payload.useBalance ? "credits" : "zooz" },
    use_credits: payload.useBalance || false,
  })

export const getPassengerEditRequestDetail = (bid: BookingId, requestId: number) =>
  transport("GET", bid, `/mmb/v1/bookings/${bid}/passengers/change_requests/${requestId}`, null)

export const deletePassengerEditRequestDetail = (bid: BookingId, requestId: number) =>
  transport("DELETE", bid, `/mmb/v1/bookings/${bid}/passengers/change_requests/${requestId}`, null)

export const manageScheduleChange = (
  bid: BookingId,
  eventId: number,
  payload: ManageScheduleChangeApiPayload,
) => transport("PATCH", bid, `/mmb/v1/bookings/${bid}/flight_events/${eventId}`, null, payload)

export const selectScheduleChangeAlternative = (
  bid: BookingId,
  eventId: number,
  payload: SelectedScheduleChangeApiPayload,
) =>
  transport("POST", bid, `/mmb/v1/bookings/${bid}/flight_events/${eventId}/orders`, null, payload)

export const submitBookingCompletion = (bid: BookingId, payload: any = {}) =>
  transport("POST", bid, `/api/v0.1/users/self/bookings/edit/${bid}`, null, payload)

export const submitPassengerChange = (bid: BookingId, payload: BookingCompletionPayload) =>
  transport("PATCH", bid, `/mmb/v1/bookings/${bid}/passengers`, null, payload)

export const getUpdatedCheckIn = (bid: BookingId) =>
  transport("GET", bid, `/mmb/v1/bookings/${bid}/checkin/fresh`, null)

export const submitTicketing = (bid: BookingId, payload: TicketingFormData, url: string) =>
  transport("POST", bid, url, null, payload)

// create guarantee request
export const confirmFlightSelection = (
  bid: BookingId,
  payload: any,
): Promise<ConfirmFlightResponse> =>
  transport("POST", bid, `/mmb/v1/bookings/${bid}/pax_ata`, null, payload)

export const getGuaranteeCoverage = (
  bid: BookingId,
  flightId: FlightId,
  retry: number = 30,
  retryTimeout: number = 5000,
): Promise<LoadGuaranteeResponse> =>
  transport(
    "GET",
    bid,
    `/mmb/v1/bookings/${bid}/pax_ata?segment_code=${flightId}`,
    undefined,
    undefined,
    retry,
  ).then(body => {
    if (!(body.guarantee_limits && body.guarantee_limits[0].price !== null) && retry > 0) {
      return new Promise(resolve => {
        setTimeout(() => {
          resolve(getGuaranteeCoverage(bid, flightId, retry - 1, retryTimeout + 1000))
        }, retryTimeout)
      })
    }
    return body
  })

export const confirmAta = (bid: BookingId, paxAtaId: number, payload: any): Promise<*> =>
  transport("PATCH", bid, `/mmb/v1/bookings/${bid}/pax_ata/${paxAtaId}`, null, payload)

export const getBaggage = (bid: BookingId, payload: any): Promise<*> =>
  transport("GET", bid, `/mmb/v1/bookings/${bid}/baggage`, null, payload)
